import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ShoppingCart, Trees, User, ChevronDown } from 'lucide-react';
import Cart from './Cart';
import { useCart } from '../context/CartContext';
import CurrencySelector from './CurrencySelector';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isShopDropdownOpen, setIsShopDropdownOpen] = useState(false);
  const { state } = useCart();
  const dropdownRef = useRef<HTMLDivElement>(null);

  const itemCount = state.items.reduce((total, item) => total + item.quantity, 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsShopDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="bg-yellow-500 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center space-x-2">
              <Trees className="h-8 w-8 text-white" />
              <span className="text-2xl font-bold text-white">Mana Mamidipallu</span>
            </Link>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-white hover:text-yellow-200">Home</Link>
            <Link to="/about-us" className="text-white hover:text-yellow-200">About Us</Link>
            
            {/* Shop Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsShopDropdownOpen(!isShopDropdownOpen)}
                className="flex items-center text-white hover:text-yellow-200"
              >
                <span>Shop</span>
                <ChevronDown className={`ml-1 h-4 w-4 transform transition-transform ${isShopDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {isShopDropdownOpen && (
                <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <Link
                      to="/shop"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50"
                      onClick={() => setIsShopDropdownOpen(false)}
                    >
                      Shop India
                    </Link>
                    <Link
                      to="/shop-canada"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50"
                      onClick={() => setIsShopDropdownOpen(false)}
                    >
                      Shop Canada
                    </Link>
                    <Link
                      to="/bulk-order"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-yellow-50"
                      onClick={() => setIsShopDropdownOpen(false)}
                    >
                      Bulk Orders
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link to="/subscriptions" className="text-white hover:text-yellow-200">Subscriptions</Link>
            <Link to="/contact" className="text-white hover:text-yellow-200">Contact</Link>
            <CurrencySelector />
            <Link to="/auth" className="text-white hover:text-yellow-200">
              <User className="h-6 w-6" />
            </Link>
            <button 
              className="text-white hover:text-yellow-200 relative"
              onClick={() => setIsCartOpen(true)}
            >
              <ShoppingCart className="h-6 w-6" />
              {itemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </button>
          </div>

          <div className="md:hidden flex items-center space-x-4">
            <CurrencySelector />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white hover:text-yellow-200"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-yellow-500">
            <Link to="/" className="block text-white hover:text-yellow-200 px-3 py-2">Home</Link>
            <Link to="/about-us" className="block text-white hover:text-yellow-200 px-3 py-2">About Us</Link>
            
            {/* Mobile Shop Section */}
            <div className="px-3 py-2">
              <div className="font-medium text-white mb-2">Shop</div>
              <div className="pl-4 space-y-2">
                <Link to="/shop" className="block text-white hover:text-yellow-200">Shop India</Link>
                <Link to="/shop-canada" className="block text-white hover:text-yellow-200">Shop Canada</Link>
                <Link to="/bulk-order" className="block text-white hover:text-yellow-200">Bulk Orders</Link>
              </div>
            </div>

            <Link to="/subscriptions" className="block text-white hover:text-yellow-200 px-3 py-2">Subscriptions</Link>
            <Link to="/contact" className="block text-white hover:text-yellow-200 px-3 py-2">Contact</Link>
            <Link to="/auth" className="block text-white hover:text-yellow-200 px-3 py-2">Login/Register</Link>
            <button 
              onClick={() => setIsCartOpen(true)}
              className="flex items-center text-white hover:text-yellow-200 px-3 py-2"
            >
              <ShoppingCart className="h-6 w-6 mr-2" />
              Cart ({itemCount})
            </button>
          </div>
        </div>
      )}

      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </nav>
  );
};

export default Navbar;